import { useMemo } from 'react';

import { PaginationState } from '@/hooks/usePagination';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';
import { toQueryParams } from '@/utils/pagination';

import { TaskFilterParams } from '../config/filterConfig';
import { useTaskFilterMapper } from './useTaskFilterMapper';

/**
 * Hook to fetch task data based on pagination state
 */
export const useTaskListData = (paginationState: PaginationState) => {
  // Convert pagination state to raw API query params
  const queryParams = useMemo(() => {
    return toQueryParams<TaskFilterParams>(paginationState);
  }, [paginationState]);

  // Build stable query params using the mapper hook
  const stableQueryParams = useTaskFilterMapper(queryParams);

  // Fetch data
  const {
    data: response,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useGetTasksQuery(stableQueryParams);

  // Extract tasks and count
  const tasks = useMemo(() => (response?.results as Task[]) || [], [response]);
  const totalCount = response?.count || 0;

  return {
    tasks,
    totalCount,
    isLoading,
    error,
    isFetching,
    refetch,
  };
};
